import React, { useState, useRef } from 'react';
import toast from 'react-hot-toast';
import {
  DocumentArrowUpIcon,
  XMarkIcon,
  CheckCircleIcon,
  DocumentIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';
import { filesService } from '../services/api';

const FileUploadZone = ({ onFilesUploaded, examId, userId }) => {
  const [files, setFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFiles(droppedFiles);
  };

  const handleFileInput = (e) => {
    const selectedFiles = Array.from(e.target.files);
    handleFiles(selectedFiles);
  };

  const handleFiles = (newFiles) => {
    // Filtrar apenas PDF, JPEG, PNG
    const validFiles = newFiles.filter((file) => {
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        toast.error(`Arquivo "${file.name}" não é suportado. Apenas PDF, JPEG e PNG são aceitos.`, {
          duration: 4000,
        });
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`Arquivo "${file.name}" é muito grande. Tamanho máximo: 10MB.`, {
          duration: 4000,
        });
        return false;
      }
      return true;
    });

    // Criar previews
    const filesWithPreviews = validFiles.map((file) => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      name: file.name,
      size: file.size,
      type: file.type,
      preview: null,
      uploaded: false,
      uploading: false,
      error: null,
    }));

    // Gerar preview para imagens
    filesWithPreviews.forEach((fileObj) => {
      if (fileObj.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          fileObj.preview = e.target.result;
          setFiles((prev) => [...prev]);
        };
        reader.readAsDataURL(fileObj.file);
      }
    });

    setFiles((prev) => [...prev, ...filesWithPreviews]);
  };

  const uploadFile = async (fileObj) => {
    const formData = new FormData();
    formData.append('file', fileObj.file);
    formData.append('examId', examId);
    formData.append('uploadedBy', userId);

    try {
      setFiles((prev) =>
        prev.map((f) => (f.id === fileObj.id ? { ...f, uploading: true } : f))
      );

      const uploadedFile = await filesService.upload(formData);

      // Verificar se a análise automática falhou
      const hasAnalysisError = uploadedFile.analysisSucceeded === false;

      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileObj.id
            ? { 
                ...f, 
                uploading: false, 
                uploaded: true, 
                uploadedData: uploadedFile,
                analysisError: hasAnalysisError // Marcar erro de análise
              }
            : f
        )
      );

      return uploadedFile;
    } catch (error) {
      console.error('Erro no upload:', error);
      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileObj.id
            ? { ...f, uploading: false, error: error.message }
            : f
        )
      );
      throw error;
    }
  };

  const uploadAllFiles = async () => {
    if (!examId || !userId) {
      toast.error('ExamId e UserId são necessários para upload', {
        duration: 4000,
      });
      return;
    }

    setUploading(true);
    const filesToUpload = files.filter((f) => !f.uploaded && !f.error);
    
    const loadingToast = toast.loading(`Enviando ${filesToUpload.length} arquivo(s)...`);

    try {
      const uploadPromises = filesToUpload.map((fileObj) => uploadFile(fileObj));
      const uploadedFiles = await Promise.all(uploadPromises);

      // Verificar se algum arquivo teve falha na análise
      const failedAnalysis = uploadedFiles.filter(f => f.analysisSucceeded === false);
      
      // Notificar componente pai
      if (onFilesUploaded) {
        onFilesUploaded(uploadedFiles);
      }

      toast.dismiss(loadingToast);
      
      if (failedAnalysis.length > 0) {
        toast.error(
          `${failedAnalysis.length} arquivo(s) com falha na análise automática. Você precisará preencher os dados manualmente.`,
          {
            duration: 6000,
            icon: '⚠️',
          }
        );
      } else {
        toast.success(`${uploadedFiles.length} arquivo(s) enviado(s) e analisado(s) com sucesso!`, {
          duration: 3000,
          icon: '✅',
        });
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error('Erro ao fazer upload de alguns arquivos. Tente novamente.', {
        duration: 5000,
      });
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (id) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (type) => {
    if (type === 'application/pdf') return DocumentIcon;
    if (type.startsWith('image/')) return PhotoIcon;
    return DocumentIcon;
  };

  return (
    <div className="space-y-4">
      {/* Drop Zone */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-4 border-dashed rounded-xl p-12 text-center transition-all cursor-pointer ${
          dragActive
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50'
        }`}
      >
        <DocumentArrowUpIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-700 font-medium mb-2">
          Arraste e solte seus arquivos aqui ou clique para selecionar
        </p>
        <p className="text-sm text-gray-500">PDF, JPEG, PNG até 10MB</p>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="application/pdf,image/jpeg,image/png"
          onChange={handleFileInput}
          className="hidden"
        />
      </div>

      {/* Files List */}
      {files.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-semibold text-gray-900">Arquivos Selecionados ({files.length})</h3>
          {files.map((fileObj) => {
            const Icon = getFileIcon(fileObj.type);
            return (
              <div
                key={fileObj.id}
                className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
              >
                {/* Preview ou Ícone */}
                <div className="flex-shrink-0">
                  {fileObj.preview ? (
                    <img
                      src={fileObj.preview}
                      alt={fileObj.name}
                      className="w-16 h-16 object-cover rounded-lg border border-gray-300"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Icon className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                </div>

                {/* File Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{fileObj.name}</p>
                  <p className="text-sm text-gray-500">{formatFileSize(fileObj.size)}</p>
                  {fileObj.error && <p className="text-sm text-red-600 mt-1">{fileObj.error}</p>}
                </div>

                {/* Status */}
                <div className="flex-shrink-0">
                  {fileObj.uploading && (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                      <span className="text-sm text-blue-600">Enviando...</span>
                    </div>
                  )}
                  {fileObj.uploaded && (
                    <CheckCircleIcon className="w-6 h-6 text-green-500" />
                  )}
                  {!fileObj.uploading && !fileObj.uploaded && (
                    <button
                      onClick={() => removeFile(fileObj.id)}
                      className="p-1 hover:bg-red-50 rounded-full transition-colors"
                    >
                      <XMarkIcon className="w-6 h-6 text-red-500" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {/* Upload Button */}
          {files.some((f) => !f.uploaded && !f.error) && (
            <button
              onClick={uploadAllFiles}
              disabled={uploading || !examId || !userId}
              className={`w-full py-3 rounded-lg font-medium transition-all ${
                uploading || !examId || !userId
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:from-blue-600 hover:to-purple-700 shadow-lg'
              }`}
            >
              {uploading ? 'Enviando...' : 'Enviar Todos os Arquivos'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default FileUploadZone;
